import { ChangeDetectorRef, Component, EventEmitter, Input, OnInit, Output } from "@angular/core";
import {
  Account,
  AccountResource,
  Assignment, AssignmentAction, AssignmentPlan,
  AssignmentResource,
  ContactResource,
  FileEntity,
  FileResource,
  FileSummary,
  PlanningSettings,
  SearchResource,
  SettingsResource,
} from "@apaq/orgkit-js";
import { CrudRepository, PageRequest } from "@apaq/leap-data-core";
import { addDays, isSameDay } from "date-fns";
import { DialogService } from "../../dialog/dialog.service";
import { Data, EditRecurringAssignmentDialogComponent, TITLE_RECURRING_SINGULAR } from "../../assignments/edit-recurring-assignment-dialog/edit-recurring-assignment-dialog.component";
import { take } from "rxjs/operators";
import log from 'loglevel';

@Component({
  selector: 'ws-assignment-editor-view',
  templateUrl: './assignment-editor.view.html',
  styleUrls: ['./assignment-editor.view.scss']
})
export class AssignmentEditorView {

  _id?: string;
  assignment?: Assignment;
  accounts: Account[] = [];
  files?: FileSummary[];
  planningSettings?: PlanningSettings;
  initialAction: AssignmentAction = 'None';
  initialDate?: Date;
  
  @Input() cancelable = false;
  @Input() accountId?: string;
  @Input() date?: Date;
  @Output() saved = new EventEmitter<void>();
  @Output() canceled = new EventEmitter<void>();

  constructor(private dialogService: DialogService,
    private _assignmentResource: AssignmentResource,
    private _contactResource: ContactResource,
    private _accountResource: AccountResource,
    private _searchResource: SearchResource ,
    private _settingsResource: SettingsResource,
    private _fileResource?: FileResource) {
      log.debug('Constructs AssignmentEditorView');
    }

  public contactSearch = async (search: string) => {
    log.debug('Searching contacts');
    const request = {
      size: 8,
      customer: true,
      query: {search}
    } as PageRequest
    const ids = (await this._searchResource.findAll(request)).content.map(e => e.id)!;
    return ids.length === 0 ? [] : (await this._contactResource?.findAll({sort:'name',size: 8,query:{id: ids}} as PageRequest))?.content;
  }

  get id(): string {
    return this._id!;
  }

  @Input()
  set id(value: string | undefined) {
    log.debug('Sets id');
    this._id = value;
    this.load();
  }

  get readonly() {
    return this.initialAction === 'Skipped' || this.initialAction === 'Completed';
  }

  get planLocked() {
    return this.initialAction !== 'None';
  }

  get editableContact() {
    return this.id == null;
  }

  handleFileUpload(file: File) {
    log.debug('Handles file upload');
    file.arrayBuffer().then(buf => {
      const fileEntity = {
        id: 'assignments/' + this.assignment!.id + '/' + file.name,
        buffer: buf
      } as unknown as FileSummary;
      this._fileResource?.save(fileEntity as FileEntity).then(file => {
        this.files?.push(file);
      })
    });
  }

  handleFileDelete(file: FileSummary) {
    log.debug('Handles file delete');
    this._fileResource?.delete(file as FileEntity).then(_ => {
      const index = this.files!.indexOf(file);
      this.files!.splice(index, 1);
    });
  }

  async save(assignment: Assignment) {
    log.debug('Saves assignment: ', assignment);
    if(assignment?.accountId == null) {
      assignment!.accountId = [...assignment!.plan.accountIds, ...this.accounts.map(a => a.id)].find(a => true)!;
    }

    let affectSeries = false;
    const isRecurring = assignment.tasks.some(t => t.reference != null);
    if(isRecurring && !isSameDay(assignment.plan.executionTime, this.initialDate!)) {
      const data: Data = { assignmentCount: 1 };
      
      const recurDialog = this.dialogService.open(EditRecurringAssignmentDialogComponent, TITLE_RECURRING_SINGULAR, { data });
      const recurResult = await recurDialog.afterClosed().pipe(take(1)).toPromise();
      if (recurResult == null) return;
      affectSeries = recurResult == 'all';
    }

    this.assignment = await this._assignmentResource!.save(assignment!, affectSeries)
    this.initialAction = this.assignment.action;
    this.saved.emit();
  }

  cancel() {
    log.debug('Cancels editor');
    this.canceled.emit();
  }

  private load() {
    log.debug('Loads assignment data');
    this.assignment = undefined;
    if(this.id != null) {
      this._assignmentResource?.findById(this.id).then(a => {
        // Trim tasks notes and messages
        a.tasks.forEach(t => [t.note, t.message, t.priorMessage] = [t.note, t.message, t.priorMessage].map(v => this.trimMessage(v)));
        this.assignment = a;
        this.initialAction = a.action;
        this.initialDate = a.plan.executionTime;

        const req: PageRequest & {path: string} = {
          size: 100,
          path: '/assignments/' + a.id
        };
        this._fileResource!.findAll(req).then(files => this.files = files.content);
        this._settingsResource?.findById('planning').then(s => this.planningSettings = <PlanningSettings>s);
      });
    } else {
      this.assignment = {
        id: null!,
        action: 'None',
        status: 'None',
        tasks: [{
          description:'',
          duration: 10,
          unitPrice: 0
        }],
        address: {
          street: '',
          apartment: '',
          postalCode: '',
          city: '',
          countryCode: ''
        },
        contactId: null!,
        meta: {},
        plan: {
          accountIds: [],
          mode: 'RoutePlanning',
          indicativeDate: addDays(new Date(), 1),
          executionTime: addDays(new Date(), 1)
        } as unknown as AssignmentPlan
      } as unknown as Assignment;
    }
    this._accountResource!.findAll({size: 100, sort: 'name'}).then(accounts => this.accounts = accounts.content);
  }

  private trimMessage(value: string) { return value != null && value != '' ? value : null!; }

}
